import { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Check, Volume2, Square, Loader2, Flame, ThumbsUp, Meh, Share2, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { useSeo } from "../seo";
import { api, USER_ID, track, money } from "../api";
import { addPick, pickListToast } from "../picks";
import { TasteDots, VegDot, DishCard, OrderButton, SectionTitle, rewardToast } from "../components";
import QuizLadder from "../QuizLadder";
import SuggestedMeal, { getMealPrefs, MealPrefsBar } from "../SuggestedMeal";
import { ALLERGEN_ICONS } from "./MenuPage";
import { useFlags, flagOn } from "../flags";
import { useSound } from "../sound";
import { useT, useLang, dishDesc } from "../i18n";
import { OwnerDishExtras } from "../content";
import { ReviewThanks } from "../ReviewThanks";
import { DishImg } from "../DishImg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const StoryScene = lazy(() => import("../StoryScene"));
const VOICE_LANGS = [
  { code: "en", label: "EN" },
  // hi/pa paused — restore here when a better TTS replaces ElevenLabs Indic voices
];

const StoryListen = ({ dish }) => {
  const { soundOn } = useSound();
  const t = useT();
  const [lang, setLang] = useState(() => localStorage.getItem("xp_lang") || "en");
  const [state, setState] = useState("idle");
  const stop = useCallback(() => {
    window.__xpStoryAudio?.pause();
    window.__xpStoryAudio = null;
    setState("idle");
  }, []);
  useEffect(() => stop, [stop]);
  const playFrom = async (i) => {
    if (i >= dish.story.cards.length) {
      api.post(`/dishes/${dish.slug}/story-complete`, { user_id: USER_ID }).catch(() => {});
      return setState("idle");
    }
    const r = await api.post(`/stories/${dish.slug}/speak`, { card_index: i, language: lang, user_id: USER_ID });
    if (!r.data.audio_url) return setState("idle");
    const a = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
    window.__xpStoryAudio = a;
    a.onended = () => playFrom(i + 1);
    await a.play();
    setState("playing");
  };
  const toggle = async () => {
    if (state === "playing") return stop();
    setState("loading");
    try {
      track("dish_story_started", { slug: dish.slug });
      await playFrom(0);
    } catch {
      setState("idle");
    }
  };
  if (!soundOn) return null;
  return (
    <div className="flex items-center gap-2 mt-4">
      <button data-testid="story-listen-btn" onClick={toggle}
        className="h-11 flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded-full px-5 text-xs font-bold tracking-widest">
        {state === "loading" ? <Loader2 size={15} className="animate-spin" /> : state === "playing" ? <Square size={13} /> : <Volume2 size={15} />}
        {state === "loading" ? t("dish.preparing") : state === "playing" ? t("dish.stop") : t("dish.listen")}
      </button>
      <div className="flex gap-1">
        {VOICE_LANGS.length > 1 && VOICE_LANGS.map((l) => (
          <button key={l.code} data-testid={`story-lang-${l.code}`} onClick={() => { setLang(l.code); stop(); }}
            className={`rounded-full w-11 h-11 text-sm font-bold border ${lang === l.code ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60"}`}>
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const GOOGLE_REVIEW_URL = "https://www.google.com/maps/search/?api=1&query=Urban%20Turban%20by%20VeerGi%202660%20N%20Halsted%20St%20Chicago";

const RATE_OPTIONS = [
  { key: "loved", labelKey: "rate.loved", Icon: Flame },
  { key: "good", labelKey: "rate.good", Icon: ThumbsUp },
  { key: "meh", labelKey: "rate.meh", Icon: Meh },
];

const RateDish = ({ slug }) => {
  const t = useT();
  const [rating, setRating] = useState(null);
  const [note, setNote] = useState("");
  const [noteSent, setNoteSent] = useState(false);
  const [reviewUrl, setReviewUrl] = useState(GOOGLE_REVIEW_URL);
  const [thanksOpen, setThanksOpen] = useState(false);
  useEffect(() => {
    api.get(`/dishes/${slug}/rating`, { params: { user_id: USER_ID } }).then((r) => setRating(r.data.rating)).catch(() => {});
    api.get("/settings").then((r) => r.data.review_url && setReviewUrl(r.data.review_url)).catch(() => {});
  }, [slug]);
  const rate = async (key) => {
    setRating(key);
    const r = await api.post(`/dishes/${slug}/rate`, { user_id: USER_ID, rating: key });
    rewardToast(r.data);
  };
  const sendNote = async () => {
    if (!note.trim()) return;
    await api.post(`/dishes/${slug}/rate`, { user_id: USER_ID, rating, note: note.trim() });
    setNoteSent(true);
  };
  return (
    <section className="px-4 pb-6">
      <div data-testid="rate-dish" className="rounded-2xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-3">{rating ? t("rate.change") : t("rate.kicker")}</p>
        <div className="flex gap-2">
          {RATE_OPTIONS.map(({ key, labelKey, Icon }) => (
            <button
              key={key}
              data-testid={`rate-${key}`}
              onClick={() => rate(key)}
              className={`flex-1 flex flex-col items-center gap-1.5 rounded-xl border py-3 transition-colors ${
                rating === key ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/10 bg-[#1A1A1A] text-white/60 hover:border-[#D4AF37]/40"
              }`}
            >
              <Icon size={18} />
              <span className="text-[9px] font-bold tracking-widest">{t(labelKey)}</span>
            </button>
          ))}
        </div>
        {rating && <p data-testid="rate-thanks" className="text-[10px] text-white/40 mt-3">{t("rate.thanks")}</p>}
        {rating && !noteSent && (
          <div className="mt-2">
            <div className="flex gap-2">
              <input
                data-testid="rate-note-input"
                value={note}
                maxLength={200}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendNote()}
                placeholder={t("rate.placeholder")}
                className="flex-1 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button
                data-testid="rate-note-send"
                onClick={sendNote}
                disabled={!note.trim()}
                className="bg-[#D4AF37] disabled:bg-[#1A1A1A] disabled:text-white/40 text-black text-[10px] font-bold rounded-full px-4 tracking-widest"
              >
                {t("rate.send")}
              </button>
            </div>
            <p className="text-[9px] text-white/35 mt-1.5">{t("rate.privacy")}</p>
          </div>
        )}
        {noteSent && <p data-testid="rate-note-sent" className="text-[10px] text-[#D4AF37] mt-2">{t("rate.noteSent")}</p>}
        {rating === "loved" && (
          <button
            data-testid="google-review-btn"
            onClick={() => {
              track("google_review_tapped", { slug });
              window.open(reviewUrl, "_blank", "noopener");
              setThanksOpen(true);
            }}
            className="w-full mt-3 flex items-center justify-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full py-3 tracking-widest hover:bg-[#D4AF37]/10"
          >
            <Flame size={13} />
            {t("rate.review")}
          </button>
        )}
        <ReviewThanks open={thanksOpen} onClose={() => setThanksOpen(false)} />
      </div>
    </section>
  );
};

const MAIN_CATS = ["Chicken Curries", "Goat & Lamb Curries", "Vegetarian Curries", "Biryani", "Urban Turban Specials"];

const MealBuilder = ({ dish }) => {
  const t = useT();
  const [prefs, setPrefs] = useState(getMealPrefs());
  if (MAIN_CATS.includes(dish.category)) {
    return (
      <section className="px-4 pb-8">
        <SectionTitle kicker={t("dish.pairKicker")} title={t("dish.pairTitle")} testId="section-meal-builder" />
        <MealPrefsBar prefs={prefs} onChange={setPrefs} />
        <SuggestedMeal mainSlug={dish.slug} prefs={prefs} autoLoad />
      </section>
    );
  }
  return <PairingMeal dish={dish} />;
};

const PairingMeal = ({ dish }) => {
  const t = useT();
  const related = dish.related_dishes || [];
  const pairSlugs = Object.values(dish.pairings || {});
  const pairs = pairSlugs.map((s) => related.find((r) => r.slug === s)).filter((p) => p && p.slug !== dish.slug);
  const [included, setIncluded] = useState(() => new Set([dish.slug, ...pairs.map((p) => p.slug)]));
  const items = [dish, ...pairs];
  const toggle = (slug) => {
    if (slug === dish.slug) return;
    const n = new Set(included);
    n.has(slug) ? n.delete(slug) : n.add(slug);
    setIncluded(n);
  };
  if (!pairs.length) return null;
  return (
    <section className="px-4 pb-8">
      <SectionTitle kicker={t("dish.pairKicker")} title={t("dish.pairTitle")} testId="section-meal-builder" />
      <div className="rounded-2xl bg-[#121212] border border-white/10 divide-y divide-white/5">
        {items.map((item) => (
          <button key={item.slug} data-testid={`meal-item-${item.slug}`} onClick={() => toggle(item.slug)}
            className="w-full flex items-center gap-3 p-3 text-left">
            <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${included.has(item.slug) ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"}`}>
              {included.has(item.slug) && <Check size={12} className="text-black" />}
            </span>
            <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
            <span className="flex-1 text-sm">{item.name}</span>
            <span className="text-sm text-[#D4AF37] font-bold">{money(item.price)}</span>
          </button>
        ))}
        <div className="flex items-center justify-between p-4">
          <span className="text-xs text-white/55">Tax and anything else is added at the till.</span>
        </div>
      </div>
      <button data-testid="order-this-meal"
        onClick={() => {
          items.filter((i) => included.has(i.slug)).forEach((i) => addPick(i, 1, true));
          pickListToast("Meal added to your order list");
        }}
        className="w-full mt-3 bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-sm tracking-widest hover:bg-[#F1C40F]">
        {t("dish.addMeal")}
      </button>
    </section>
  );
};

export default function DishPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const flags = useFlags();
  const t = useT();
  const { lang } = useLang();
  const [dish, setDish] = useState(null);
  const [fav, setFav] = useState(false);
  const [scene, setScene] = useState(null);
  const [sceneOpen, setSceneOpen] = useState(false);
  const [allSlugs, setAllSlugs] = useState([]);
  const touch = useRef(null);

  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: dish?.name,
    description: dish?.description ? String(dish.description).slice(0, 158) : undefined,
    path: `/experience/dish/${slug}`,
    jsonLd: dish ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://myurbanpassport.com/experience" },
        { "@type": "ListItem", position: 2, name: "Menu", item: "https://myurbanpassport.com/menu" },
        { "@type": "ListItem", position: 3, name: dish.name, item: `https://myurbanpassport.com/experience/dish/${slug}` },
      ],
    } : null,
  });

  useEffect(() => {
    api.get("/dishes").then((r) => setAllSlugs(r.data.map((d) => d.slug))).catch(() => {});
  }, []);

  const goDish = useCallback((dir) => {
    const idx = allSlugs.indexOf(slug);
    if (allSlugs.length < 2 || idx === -1) return;
    const next = allSlugs[(idx + dir + allSlugs.length) % allSlugs.length];
    track("dish_swipe_nav", { from: slug, to: next, dir });
    navigate(`/experience/dish/${next}`);
  }, [allSlugs, slug, navigate]);

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dy) < 50) goDish(dx < 0 ? 1 : -1);
  };

  useEffect(() => {
    setScene(null);
    setSceneOpen(false);
    api.get(`/story-scene/${slug}`, { params: { user_id: USER_ID } }).then((r) => setScene(r.data)).catch(() => setScene(null));
    api.get(`/dishes/${slug}`)
      .then((r) => {
        if (r.data.redirect_to) { navigate(`/experience/dish/${r.data.redirect_to}`, { replace: true }); return; }
        setDish(r.data);
        api.post(`/dishes/${slug}/explore`, { user_id: USER_ID }).then((res) => rewardToast(res.data));
        api.get(`/passport/${USER_ID}`).then((res) => setFav(res.data.user.favorites.includes(slug)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div data-testid="dish-not-found" className="p-6 pt-16 text-center">
        <p className="font-display text-3xl font-bold">Dish not found</p>
        <p className="text-sm text-white/50 mt-2">This dish isn't on the Urban menu right now.</p>
        <Link to="/experience/explore" className="inline-block mt-6 bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 py-3 tracking-widest">EXPLORE THE MENU</Link>
      </div>
    );
  if (!dish) return <div className="p-6 text-white/50 text-sm">Loading dish…</div>;

  const toggleFav = async () => {
    const r = await api.post(`/favorites/${slug}`, { user_id: USER_ID });
    setFav(r.data.favorited);
  };

  const shareDish = async () => {
    const url = `${window.location.origin}/api/share/dish/${slug}`;
    track("dish_shared", { slug });
    try {
      if (navigator.share) {
        await navigator.share({ title: `${dish.name} — Urban by VeerGi`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — paste it in WhatsApp or Instagram", {
          style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" },
        });
      }
    } catch {}
  };

  const story = dish.story;

  return (
    <div data-testid={`dish-page-${slug}`}>
      <div className="relative h-[42vh] min-h-[300px] onphoto" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="dish-hero">
        <DishImg src={dish.image} alt={dish.name} className="absolute inset-0 w-full h-full object-cover" />
        {dish.category !== "Drinks & Desserts" && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="dish-live-photo">
            <span className="dish-steam" style={{ left: "38%" }} />
            <span className="dish-steam" style={{ left: "52%", animationDelay: "1.6s" }} />
            <span className="dish-steam" style={{ left: "63%", animationDelay: "3.1s" }} />
            <span className="dish-shimmer" />
          </div>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,20,12,0.9) 0%, rgba(30,20,12,0.5) 55%, rgba(30,20,12,0.15) 100%)" }} />
        <button data-testid="share-dish-btn" onClick={shareDish} aria-label="Share this dish"
          className="absolute top-4 right-16 z-10 bg-black/50 backdrop-blur rounded-full p-2.5">
          <Share2 size={18} className="text-white" />
        </button>
        <button data-testid="favorite-btn" onClick={toggleFav} aria-label="Save to favorites"
          className="absolute top-4 right-4 z-10 bg-black/50 backdrop-blur rounded-full p-2.5">
          <Heart size={18} className={fav ? "text-[#E25822] fill-[#E25822]" : "text-white"} />
        </button>
        {allSlugs.length > 1 && (
          <>
            <button data-testid="dish-prev-btn" onClick={() => goDish(-1)} aria-label="Previous dish"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center active:scale-95 transition-transform">
              <ChevronLeft size={22} className="text-white" />
            </button>
            <button data-testid="dish-next-btn" onClick={() => goDish(1)} aria-label="Next dish"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center active:scale-95 transition-transform">
              <ChevronRight size={22} className="text-white" />
            </button>
          </>
        )}
        <div className="absolute bottom-0 p-5 z-10 pointer-events-none">
          <div className="flex items-center gap-2 mb-1">
            <VegDot veg={dish.vegetarian} />
            <span className="text-[10px] tracking-[0.25em] text-[#D4AF37]">{dish.category.toUpperCase()}</span>
            <span className="flex items-center gap-0.5 bg-black/40 rounded-full px-2 py-0.5" title={`Spice ${dish.spice}/5`}>
              {[1, 2, 3].map((i) => <Flame key={i} size={11} className={i <= Math.ceil(dish.spice / 2) ? "text-[#E25822]" : "text-white/25"} />)}
              <span className="text-[10px] text-white/80 ml-1">SPICE {dish.spice}/5</span>
            </span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter leading-none">{dish.name}</h1>
          <p className="text-2xl font-black text-[#D4AF37] mt-2" data-testid="dish-price">{money(dish.price)}</p>
          {dish.available === false && (
            <p data-testid="dish-unavailable"
              className="inline-block mt-2 bg-black/60 border border-[#E25822]/60 text-[#E25822] text-[10px] font-black tracking-[0.25em] rounded-full px-3 py-1">
              UNAVAILABLE TODAY — ASK YOUR SERVER
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-4xl xl:max-w-6xl 2xl:max-w-[1400px]">
      <div className="px-4 pt-4 pb-2">
        <p className="text-sm text-white/70">{dishDesc(dish, lang)}</p>
        {(dish.allergens || []).length > 0 && (
          <div data-testid="dish-allergens" className="flex items-center gap-2 mt-3 flex-wrap">
            {dish.allergens.map((a) => {
              const I = ALLERGEN_ICONS[a];
              const hit = (getMealPrefs().allergens || []).includes(a);
              return (
                <span key={a} data-testid={`dish-allergen-${a}`}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[9px] font-bold tracking-widest ${hit ? "border-[#E25822]/70 bg-[#E25822]/10 text-[#E25822]" : "border-white/15 text-white/50"}`}>
                  {I && <I size={11} />} CONTAINS {a.toUpperCase()}{hit ? " — YOUR ALLERGY" : ""}
                </span>
              );
            })}
            <span className="text-[9px] text-white/35">A guide only — confirm with your server</span>
          </div>
        )}
        <OrderButton dish={dish} className="w-full mt-4" testId="dish-order-btn" />
      </div>

      <section className="px-4 py-6">
        <SectionTitle kicker={t("dish.tasteKicker")} title={t("dish.tasteTitle")} testId="section-taste" />
        <div className="rounded-2xl bg-[#121212] border border-white/10 p-4">
          <TasteDots label="CREAMY" value={dish.creamy} />
          <TasteDots label="SPICY" value={dish.spice} />
          <TasteDots label="SMOKY" value={dish.smoky} />
          <TasteDots label="RICH" value={dish.rich} />
          <TasteDots label="SWEET" value={dish.sweet} />
        </div>
      </section>

      <OwnerDishExtras dish={dish} onSaved={(x) => setDish((d) => ({ ...d, ...x }))} />


      {story && flagOn(flags, "dish_stories") && (
        <section id="story" className="px-4 pb-8" data-testid="section-story">
          <SectionTitle kicker={t("dish.storyKicker")} title={t("dish.storyTitle")} testId="section-story-title" />
          {scene && (
            <div className="mb-4">
              <button
                data-testid="story-live-btn"
                onClick={() => { setSceneOpen(true); track("story_scene_opened", { slug }); }}
                onMouseEnter={() => window.dispatchEvent(new CustomEvent("xp-mascot", { detail: { type: "hint" } }))}
                className="story-live-btn w-full h-14 rounded-full bg-[#D4AF37] hover:bg-[#F0C742] text-black font-black tracking-[0.14em] text-sm flex items-center justify-center gap-2"
              >
                <Sparkles size={16} /> {t("story.live")}
              </button>
              <p className="text-[10px] text-white/40 tracking-wide mt-1.5 text-center">{t("story.liveSub")}</p>
            </div>
          )}
          {story.review_status && <p className="text-xs text-white/40 mb-3 tracking-wide">{story.review_status}</p>}
          {dish.story_video_url && (
            <video data-testid="story-video" src={dish.story_video_url.startsWith("http") ? dish.story_video_url : `${BACKEND_URL}${dish.story_video_url}`}
              poster={dish.image} controls
              className="w-full max-h-96 rounded-2xl border border-[#D4AF37]/25 mb-4 bg-black"
              autoPlay loop muted playsInline preload="metadata"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div className="space-y-3">
            {story.cards.map((c, i) => (
              <div key={i} data-testid={`story-card-${i}`} className="rounded-2xl bg-[#121212] border border-white/10 p-5 xp-card-shadow">
                <p className="text-xs tracking-[0.3em] text-[#D4AF37] font-bold mb-1">PART {i + 1} · {story.title.toUpperCase()}</p>
                <h3 className="font-display text-xl font-semibold">{c.title}</h3>
                <p className="text-base leading-relaxed mt-2 text-white/75">{c.text}</p>
              </div>
            ))}
          </div>
          {!scene && <StoryListen dish={dish} />}
        </section>
      )}

      {sceneOpen && scene && (
        <Suspense fallback={null}>
          <StoryScene slug={slug} scene={scene} dish={dish} onClose={() => setSceneOpen(false)} />
        </Suspense>
      )}

      {(dish.has_quiz || dish.quiz) && flagOn(flags, "food_quizzes") && <QuizLadder slug={slug} />}

      {flagOn(flags, "meal_builder") && <MealBuilder dish={dish} />}

      {dish.related_dishes?.length > 0 && (
        <section className="pb-6">
          <div className="px-4"><SectionTitle kicker={t("dish.relatedKicker")} title={t("dish.relatedTitle")} testId="section-related" /></div>
          <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar px-4">
            {dish.related_dishes.map((r) => <DishCard key={r.slug} dish={r} compact />)}
          </div>
        </section>
      )}

      <RateDish slug={slug} />

      <div className="px-4 pb-10">
        <OrderButton dish={dish} className="w-full" testId="dish-order-btn-bottom" />
      </div>
      </div>
    </div>
  );
}
